import React from 'react';
import { Popover, Space, Tag, Tooltip } from 'antd';
import dayjs from 'dayjs';
import isEmpty from 'lodash/isEmpty';
import weekday from 'dayjs/plugin/weekday';
import localizedFormat from 'dayjs/plugin/localizedFormat';
import localeData from 'dayjs/plugin/localeData';
import { getLocaleAwareDaysArray } from '@/utils/openHours';
import { useIsMobile } from '@/hooks/useResponsive';
import styles from './index.less';
import { COLORS } from '@/constants/colors';

/** "Mo. – Fr., So." style summary from the locale-ordered availability flags. */
const summarizeDays = (labels: string[], availableFlags: boolean[]): string => {
  const ranges: string[] = [];
  let rangeStart = -1;
  availableFlags.forEach((available, index) => {
    if (available && rangeStart === -1) rangeStart = index;
    const rangeEnds = rangeStart !== -1 && (!available || index === availableFlags.length - 1);
    if (rangeEnds) {
      const rangeEnd = available ? index : index - 1;
      ranges.push(rangeEnd > rangeStart ? `${labels[rangeStart]} – ${labels[rangeEnd]}` : labels[rangeStart]);
      rangeStart = -1;
    }
  });
  return ranges.join(', ');
};

dayjs.extend(weekday);
dayjs.extend(localizedFormat);
dayjs.extend(localeData);

const AvailabilityGlance = ({
                        availability,
                      }: {
  availability: { day: string; start: string; end: string }[];
}) => {


  const availabilityMap = Array.isArray(availability)
    ? (availability || []).reduce((acc, { day, start, end }) => {
        const timeRange = `${dayjs(start, 'HH:mm').format('HH:mm')} - ${dayjs(end, 'HH:mm').format('HH:mm')}`;
        if (!acc[day]) {
          acc[day] = [];
        }
        if (!acc[day].includes(timeRange)) {
          acc[day].push(timeRange);
        }
        return acc;
      }, {} as Record<string, string[]>)
    : (availability || {});

  const localeAwareDays = getLocaleAwareDaysArray();
  const isMobile = useIsMobile();

  const days = localeAwareDays.map((day, index) => {
    const daySlots = availabilityMap?.[day];
    return {
      day,
      daySlots,
      available: !isEmpty(daySlots),
      label: dayjs().weekday(index).format('ddd'), // e.g. 'Mon', 'Tue'
    };
  });

  // Phones: seven chips don't fit a card row — one tappable summary tag opens the details.
  if (isMobile) {
    const summary = summarizeDays(days.map((d) => d.label), days.map((d) => d.available));
    if (!summary) return <Tag bordered={false}>–</Tag>;
    return (
      <Popover
        trigger="click"
        content={
          <Space direction="vertical" size={2}>
            {days.filter((d) => d.available).map(({ day, label, daySlots }) => (
              <Space key={day} size={4}>
                <Tag color={COLORS.SUCCESS} bordered={false} className={styles.availabilityTag}>
                  {label}
                </Tag>
                <small>
                  {Array.isArray(daySlots) ? daySlots.sort().join(', ') : daySlots}
                </small>
              </Space>
            ))}
          </Space>
        }
      >
        <Tag
          color={COLORS.SUCCESS}
          bordered={false}
          className={styles.availabilityTag}
          onClick={(e) => e.stopPropagation()}
        >
          {summary}
        </Tag>
      </Popover>
    );
  }

  return (
    <Space size={1}>
      {days.map(({ day, label, available, daySlots }) => {
        const tooltipTitle = available ? (
          <Space direction="vertical" size={0}>
            {Array.isArray(daySlots) ? daySlots.sort().map((slot, i) => <div key={i}>{slot}</div>) : daySlots}
          </Space>
        ) : '';

        return (
          <Tooltip title={tooltipTitle} key={day}>
            <Tag
              color={available ? COLORS.SUCCESS : COLORS.WARNING}
              bordered={false}
              className={styles.availabilityTag}
            >
              {label}
            </Tag>
          </Tooltip>
        );
      })}
    </Space>
  );
};

export default AvailabilityGlance;
